import { WithChildren } from '../../blanket/blanket-product/hierarchy-utils';
import { IPRAttributes } from './ipr';
import { mergeIprAttributes } from './ipr/merge-ipr-attributes';

export interface ResolvedIpr {
  /** Attributes cascading in from ancestors (this node's own attributes not yet applied). */
  inheritedIpr: Partial<IPRAttributes>;
  /** `inheritedIpr` merged with this node's own attributes — the blob that drives compute. */
  resolvedIpr: Partial<IPRAttributes>;
}

/**
 * Resolve IPR attribute inheritance top-down over a product hierarchy, mirroring
 * {@link propagateTaxonomyMappingsRecursive}: each node's `resolvedIpr` is what it inherits from
 * its ancestors merged with its own attributes (scalars override, arrays union — see
 * {@link mergeIprAttributes}), and that resolved blob is what cascades to its children.
 *
 * Nodes expose their own attributes as `ownIpr` so the resolver stays agnostic to whether the
 * caller's node is a frontend `PiuProductState` or a backend `BlanketProduct` — each computes its
 * own merged IPR (auto-detected + manual edits) before calling.
 */
export function resolveIprAttributesRecursive<T extends { ownIpr: Partial<IPRAttributes> }>(
  products: Array<WithChildren<T>>,
  inheritedIpr: Partial<IPRAttributes> = {},
): Array<WithChildren<T & ResolvedIpr>> {
  return products.map(product => {
    const resolvedIpr = mergeIprAttributes(inheritedIpr, product.ownIpr);
    const productWithResolvedIpr = { ...product, inheritedIpr, resolvedIpr };
    return {
      ...productWithResolvedIpr,
      children: resolveIprAttributesRecursive(product.children, resolvedIpr),
    };
  });
}
